/**
 * MONSTER TOWN — FLOOR & WALLPAPER STYLES. OWNED BY THE INTERIORS CREW.
 *
 * DATA, not engine code. A new wallpaper is an entry in `WALL_STYLES` and
 * nothing else changes — the paint can cycles it, the save round-trips it, the
 * shell rebuilds with it.
 *
 * Every texture is drawn in code (zero external assets) at `ART.textureSize`
 * and cached by a key that INCLUDES the tiling, so two rooms of different sizes
 * can share a style without fighting over one texture's `repeat`.
 *
 * Materials are cached per (style, tiling) — a handful for the whole game, not
 * one per room rebuild.
 */

import * as THREE from 'three'
import { PALETTE, hex, type PaletteName } from '../core/palette'
import { toon } from '../render/kit'

/**
 * SURFACE TEXTURES ARE DRAWN AT 256, NOT THE KIT'S 128.
 *
 * A floor texture covers four grid tiles and an interior camera sits close
 * enough that one grid tile fills a good slice of an iPad. At 128 that is ~32
 * device pixels per tile and the grout lines came back as soft brown mush with
 * visible streaking — "the grout bands are soft, blurry and continue under the
 * wall base". Doubling the source resolution is two hundred kilobytes of canvas
 * for the whole game (the cache is keyed per style, a handful of entries) and it
 * is the difference between a drawn floor and a photograph of a floor.
 */
const SURFACE_TEX = 256

const texCache = new Map<string, THREE.Texture>()

function surfaceTexture(key: string, draw: (c: CanvasRenderingContext2D, s: number) => void): THREE.Texture {
  const cached = texCache.get(key)
  if (cached !== undefined) return cached
  const canvas = document.createElement('canvas')
  canvas.width = SURFACE_TEX
  canvas.height = SURFACE_TEX
  const c = canvas.getContext('2d')
  if (c === null) throw new Error('interior style: no 2d context')
  draw(c, SURFACE_TEX)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  // Anisotropy matters here and nowhere else: a floor plane seen at a 34-degree
  // pitch is the one surface in this game sampled at a grazing angle.
  tex.anisotropy = 4
  texCache.set(key, tex)
  return tex
}

export type PatternKind = 'plain' | 'planks' | 'tiles' | 'checker' | 'weave' | 'stripes' | 'dots' | 'hearts' | 'stars'

export interface SurfaceStyle {
  readonly id: string
  /** Dev-only. Never shown to a kid — LAW #4. */
  readonly devName: string
  readonly base: PaletteName
  readonly accent: PaletteName
  readonly pattern: PatternKind
}

/** Floors a kid can cycle through by tapping the floor sample. */
export const FLOOR_STYLES: readonly SurfaceStyle[] = [
  { id: 'wood', devName: 'Wooden boards', base: 'floorWood', accent: 'wood', pattern: 'planks' },
  { id: 'tile', devName: 'Cream tiles', base: 'floorTile', accent: 'pathEdge', pattern: 'tiles' },
  { id: 'checker', devName: 'Checkerboard', base: 'wallCream', accent: 'wallMint', pattern: 'checker' },
  { id: 'carpet', devName: 'Warm carpet', base: 'floorRug', accent: 'fabric', pattern: 'weave' },
  { id: 'sky', devName: 'Sky tiles', base: 'wallpaperBlue', accent: 'glassDark', pattern: 'tiles' },
  { id: 'stone', devName: 'Stone floor', base: 'rock', accent: 'rockDark', pattern: 'checker' },
]

/** Wallpapers a kid can cycle through by tapping the paint can. */
export const WALL_STYLES: readonly SurfaceStyle[] = [
  { id: 'cream', devName: 'Plain cream', base: 'wallpaperCream', accent: 'sand', pattern: 'plain' },
  { id: 'mint', devName: 'Mint stripes', base: 'wallpaperMint', accent: 'wallMint', pattern: 'stripes' },
  { id: 'blue', devName: 'Blue dots', base: 'wallpaperBlue', accent: 'glassDark', pattern: 'dots' },
  { id: 'pink', devName: 'Pink hearts', base: 'wallPink', accent: 'uiHeart', pattern: 'hearts' },
  { id: 'lilac', devName: 'Lilac stars', base: 'wallLilac', accent: 'monsterLilac', pattern: 'stars' },
  { id: 'sunny', devName: 'Sunny stripes', base: 'sunWarm', accent: 'uiCoin', pattern: 'stripes' },
]

export function floorStyle(index: number): SurfaceStyle {
  const s = FLOOR_STYLES[((index % FLOOR_STYLES.length) + FLOOR_STYLES.length) % FLOOR_STYLES.length]
  return s ?? { id: 'wood', devName: 'Wooden boards', base: 'floorWood', accent: 'wood', pattern: 'planks' }
}

export function wallStyle(index: number): SurfaceStyle {
  const s = WALL_STYLES[((index % WALL_STYLES.length) + WALL_STYLES.length) % WALL_STYLES.length]
  return s ?? { id: 'cream', devName: 'Plain cream', base: 'wallpaperCream', accent: 'sand', pattern: 'plain' }
}

// ---------------------------------------------------------------------------
// pattern drawing (canvas 2d -> texture, zero external assets)
// ---------------------------------------------------------------------------

/**
 * `#rrggbb` -> `rgba(r,g,b,a)`. Canvas gradients interpolate in straight
 * (non-premultiplied) RGBA, so fading a colour out to `rgba(0,0,0,0)` walks it
 * through black and leaves a dirty fringe. Fade to the SAME colour at alpha 0.
 */
function rgbaOf(hexStr: string, a: number): string {
  const n = Number.parseInt(hexStr.replace('#', ''), 16)
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`
}

function drawPattern(c: CanvasRenderingContext2D, s: number, style: SurfaceStyle): void {
  c.fillStyle = PALETTE[style.base]
  c.fillRect(0, 0, s, s)
  c.strokeStyle = PALETTE[style.accent]
  c.fillStyle = PALETTE[style.accent]
  // Thinner than it was relative to the cell. At 256 a s/42 line is six pixels
  // wide, which is a 2.5cm grout band at interior scale — a trench, not a joint.
  c.lineWidth = Math.max(1, s / 90)
  c.lineCap = 'square'

  switch (style.pattern) {
    case 'plain': {
      // Not truly flat: a whisper of grain keeps a big wall from looking dead.
      c.globalAlpha = 0.1
      for (let i = 0; i < 26; i++) {
        const x = (i * 97) % s
        const y = (i * 151) % s
        c.fillRect(x, y, s / 26, s / 26)
      }
      c.globalAlpha = 1
      break
    }
    case 'planks': {
      /*
       * BOARDS THAT ARE NOT ALL THE SAME BOARD.
       *
       * ROUND-3 FIX. This drew four seam lines and nothing else, so the living
       * room floor was one flat colour with a grid on it and the critic's read
       * was fair: "the middle two thirds of the room is bare flat brown plank
       * with no pattern variation". Real boards alternate in tone and have grain
       * and knots in them; four rows at four different lightnesses, plus a
       * couple of grain strokes per board, is enough to stop the eye sliding
       * off — and it costs nothing, because it is baked once into a 256-pixel
       * canvas that every wooden floor in the game shares.
       */
      const rows = 4
      const h = s / rows
      const shade = [0.06, -0.05, 0.1, -0.02]
      for (let r = 0; r < rows; r++) {
        const k = shade[r] ?? 0
        c.globalAlpha = Math.abs(k)
        c.fillStyle = k > 0 ? '#ffffff' : PALETTE[style.accent]
        c.fillRect(0, r * h, s, h)
        // grain: two long hairlines and one knot per board
        c.globalAlpha = 0.13
        c.fillStyle = PALETTE[style.accent]
        c.fillRect(0, r * h + h * 0.3, s, Math.max(1, s / 200))
        c.fillRect(0, r * h + h * 0.68, s, Math.max(1, s / 220))
        c.globalAlpha = 0.2
        c.beginPath()
        c.ellipse(((r * 83) % s) + s * 0.1, r * h + h * 0.5, s / 44, s / 90, 0, 0, Math.PI * 2)
        c.fill()
      }
      c.globalAlpha = 0.42
      c.fillStyle = PALETTE[style.accent]
      for (let r = 0; r < rows; r++) {
        c.beginPath()
        c.moveTo(0, r * h)
        c.lineTo(s, r * h)
        c.stroke()
        // staggered board ends
        const x = ((r % 2) * s) / 2 + s / 4
        c.beginPath()
        c.moveTo(x, r * h)
        c.lineTo(x, (r + 1) * h)
        c.stroke()
      }
      c.globalAlpha = 1
      break
    }
    case 'tiles': {
      const n = 4
      const g = s / n
      c.globalAlpha = 0.38
      for (let i = 0; i <= n; i++) {
        c.beginPath()
        c.moveTo(i * g, 0)
        c.lineTo(i * g, s)
        c.moveTo(0, i * g)
        c.lineTo(s, i * g)
        c.stroke()
      }
      c.globalAlpha = 1
      break
    }
    case 'checker': {
      const n = 4
      const g = s / n
      for (let y = 0; y < n; y++) {
        for (let x = 0; x < n; x++) {
          if ((x + y) % 2 === 0) continue
          c.fillRect(x * g, y * g, g, g)
        }
      }
      break
    }
    case 'weave': {
      c.globalAlpha = 0.35
      const step = s / 16
      for (let i = 0; i < 16; i++) {
        c.fillRect(i * step, 0, step / 2, s)
        c.fillRect(0, i * step, s, step / 2)
      }
      c.globalAlpha = 1
      break
    }
    case 'stripes': {
      /*
       * SOFT-SHOULDERED STRIPES. Flat-filled bars at 45% alpha gave every
       * papered wall a hard light/dark terminator down its face, which under a
       * three-band toon ramp read as mach banding ("the wall's generated
       * vertical stripes show mach banding"). A stripe with a few pixels of
       * ramp on each edge is still obviously a stripe and has no step for the
       * eye to catch on.
       */
      const step = s / 6
      const shoulder = step * 0.22
      for (let i = 0; i < 6; i += 2) {
        const x0 = i * step
        const g = c.createLinearGradient(x0, 0, x0 + step, 0)
        g.addColorStop(0, rgbaOf(PALETTE[style.accent], 0))
        g.addColorStop(shoulder / step, PALETTE[style.accent])
        g.addColorStop(1 - shoulder / step, PALETTE[style.accent])
        g.addColorStop(1, rgbaOf(PALETTE[style.accent], 0))
        c.globalAlpha = 0.38
        c.fillStyle = g
        c.fillRect(x0, 0, step, s)
      }
      c.globalAlpha = 1
      c.fillStyle = PALETTE[style.accent]
      break
    }
    case 'dots': {
      c.globalAlpha = 0.5
      for (let y = 0; y < 4; y++) {
        for (let x = 0; x < 4; x++) {
          const ox = (y % 2) * (s / 8)
          c.beginPath()
          c.arc(x * (s / 4) + s / 8 + ox, y * (s / 4) + s / 8, s / 26, 0, Math.PI * 2)
          c.fill()
        }
      }
      c.globalAlpha = 1
      break
    }
    case 'hearts': {
      c.globalAlpha = 0.55
      for (let y = 0; y < 3; y++) {
        for (let x = 0; x < 3; x++) {
          const cx = x * (s / 3) + s / 6 + (y % 2) * (s / 6)
          const cy = y * (s / 3) + s / 6
          const r = s / 22
          c.beginPath()
          c.arc(cx - r, cy - r * 0.4, r, 0, Math.PI * 2)
          c.arc(cx + r, cy - r * 0.4, r, 0, Math.PI * 2)
          c.moveTo(cx - r * 2, cy)
          c.lineTo(cx, cy + r * 2.1)
          c.lineTo(cx + r * 2, cy)
          c.closePath()
          c.fill()
        }
      }
      c.globalAlpha = 1
      break
    }
    case 'stars': {
      c.globalAlpha = 0.6
      for (let i = 0; i < 9; i++) {
        const cx = ((i * 53) % s) + s / 12
        const cy = ((i * 89) % s) + s / 12
        const r = s / 24
        c.beginPath()
        for (let k = 0; k < 10; k++) {
          const rr = k % 2 === 0 ? r : r * 0.45
          const ang = (k / 10) * Math.PI * 2 - Math.PI / 2
          const px = cx + Math.cos(ang) * rr
          const py = cy + Math.sin(ang) * rr
          if (k === 0) c.moveTo(px, py)
          else c.lineTo(px, py)
        }
        c.closePath()
        c.fill()
      }
      c.globalAlpha = 1
      break
    }
  }
}

// ---------------------------------------------------------------------------
// materials
// ---------------------------------------------------------------------------

const matCache = new Map<string, THREE.Material>()

/**
 * How many world units one texture repeat covers. Every pattern draws a 4x4
 * block, so 4 units per repeat means ONE PATTERN CELL PER GRID TILE at interior
 * scale. That is the whole fix for "the floor tile grid is clipped mid-tile at
 * every edge and does not register to the walls": rooms are an integer number of
 * tiles across, so a repeat of `width / 4` always lands a pattern cell boundary
 * exactly on the room edge, and every visible tile is a whole tile.
 */
export const SURFACE_UNITS_PER_REPEAT = 4

/** Repeat count for a run of `lengthUnits`. Float on purpose — see above. */
export function surfaceRepeat(lengthUnits: number): number {
  return Math.max(0.25, lengthUnits / SURFACE_UNITS_PER_REPEAT)
}

/**
 * A toon material carrying the style's generated pattern, tiled `repX` x `repZ`.
 *
 * Cached per (style, tiling) — and callers now pass tilings derived from the
 * ACTUAL length of the surface, so a 9-unit wall and a 7-unit wall get the same
 * stripe width instead of the same stripe COUNT. ("The wall's vertical stripe
 * texture is a different scale on each of the two walls.") The extra materials
 * that costs are a handful; the alternative is a room whose two walls do not
 * look like they came out of the same tin of paint.
 */
export function surfaceMaterial(style: SurfaceStyle, repX: number, repZ: number): THREE.Material {
  const rx = Math.round(repX * 100) / 100
  const rz = Math.round(repZ * 100) / 100
  const key = `${style.id}:${rx}x${rz}`
  const cached = matCache.get(key)
  if (cached !== undefined) return cached
  // One canvas per style, many materials per canvas: clone the shared texture so
  // each tiling gets its own repeat without fighting over one Texture object.
  const base = surfaceTexture(`surface:${style.id}`, (c, s) => drawPattern(c, s, style))
  const tex = base.clone()
  tex.repeat.set(rx, rz)
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.needsUpdate = true
  const m = new THREE.MeshToonMaterial({ color: hex(style.base), map: tex })
  matCache.set(key, m)
  return m
}

/** Flat companion colour for skirting boards, door frames and trims. */
export function trimMaterial(style: SurfaceStyle): THREE.Material {
  return toon(style.accent)
}
